import { boundedCache, cached } from './agents-bird.cache';
import { birdFrame, renderBirdSilhouette } from './agents-bird.draw';
import {
  cellsAcross,
  type HalftoneGrid,
  halftoneGrid,
  halftoneRadii,
  type HalftoneTone,
  isCompact,
  latticeGrid,
} from './agents-bird.halftone';
import { type BirdSpecies } from './agents-bird.species';
import { toneField, type ToneField } from './agents-bird.tone';

export type BirdFrames = {
  grid: HalftoneGrid;
  radii: (wingBeat?: number) => Float32Array;
  mask: ((wingBeat: number) => HTMLCanvasElement) | null;
};

const MIN_FIELD_SIZE = 96;
const MAX_FIELD_SIZE = 220;
const COMPACT_MIN_STEP = 2;
const COMPACT_FLOOR = 0.3;
const TINY_FLOOR = 0.5;
const TINY_SIZE = 17;
const FLOOR_EASE_END = 48;
const PRINT_TONE: HalftoneTone = { floor: 0.05, clipped: false };

const compactTone = (cssSize: number): HalftoneTone => {
  const progress = Math.min(
    1,
    Math.max(0, (cssSize - TINY_SIZE) / (FLOOR_EASE_END - TINY_SIZE))
  );
  return {
    floor: TINY_FLOOR + (COMPACT_FLOOR - TINY_FLOOR) * progress,
    clipped: true,
  };
};

const FIELD_CACHE_LIMIT = 24;
const RADII_CACHE_LIMIT = 384;
const MASK_CACHE_LIMIT = 128;

const fields = boundedCache<ToneField>(FIELD_CACHE_LIMIT);
const radii = boundedCache<Float32Array>(RADII_CACHE_LIMIT);
const masks = boundedCache<HTMLCanvasElement>(MASK_CACHE_LIMIT);

export const birdFrames = (
  species: BirdSpecies,
  cssSize: number,
  pixels: number,
  dark: boolean
): BirdFrames => {
  const compact = isCompact(cssSize);
  const key = `${species}:${cssSize}:${pixels}:${dark}`;
  const frame = birdFrame(species);
  const grid = compact
    ? latticeGrid(
        pixels,
        Math.max(COMPACT_MIN_STEP, Math.round(pixels / cssSize))
      )
    : halftoneGrid(pixels, cellsAcross(cssSize));
  const fieldSize = Math.min(
    MAX_FIELD_SIZE,
    Math.max(MIN_FIELD_SIZE, Math.round(pixels * 1.5))
  );
  const radiiFor = (wingBeat = 0) =>
    cached(radii, `${key}:${wingBeat}`, () =>
      halftoneRadii(
        grid,
        cached(fields, `${species}:${fieldSize}:${wingBeat}`, () =>
          toneField(species, frame, fieldSize, wingBeat)
        ),
        pixels,
        dark,
        compact ? compactTone(cssSize) : PRINT_TONE
      )
    );
  const maskFor = (wingBeat: number) =>
    cached(masks, `${species}:${pixels}:${wingBeat}`, () =>
      renderBirdSilhouette(species, pixels, wingBeat)
    );
  return {
    grid,
    radii: radiiFor,
    mask: compact ? maskFor : null,
  };
};
